// decideWaiting — chat's decisions for messages sent while the assistant
// works (PS-61; pen "Studio · Action panel · Working"). Sending while a turn
// runs posts the message at once, waiting; each action on waiting messages
// (Send now, Edit, Delete, Undo) asks its own port; what changed arrives on
// the turn stream (folded in app/turnStream.ts), so no re-read is spent.
// Edit puts the texts into the composer. Pure.

import * as Core from "@bilda/core";
import { exhaustSwitchCase, Result } from "@bilda/utils";
import type { AuthorLocation } from "../../app/authorLocation.js";
import type {
    TurnStreamEvent,
    TurnStreamIds,
    TurnWaitingEvent,
} from "../../app/turnStream.js";
import type { ChatEntry } from "../../components/Chat/types.js";
import type { ChatCommand } from "./command.js";
import type { ChatDecision } from "./effect.js";
import type { ChatError } from "./error.js";
import type { ChatEvent } from "./event.js";
import {
    type ChatSession,
    chatSessionFor,
    resumeActivityId,
    turnLocalIds,
} from "./model.js";
import type { ChatState } from "./state.js";

type WaitingCommand = Extract<
    ChatCommand,
    {
        readonly type:
            | "waiting_message_accepted"
            | "waiting_action_settled"
            | "waiting_message_refused"
            | "waiting_taken_back_to_edit"
            | "requested_waiting_edit"
            | "requested_waiting_send_now"
            | "requested_waiting_delete"
            | "requested_waiting_undo";
    }
>;

// How many messages wait in a session's thread.
const waitingCount = (conversation: readonly ChatEntry[]): number =>
    conversation.filter(
        (e) => e.type === "author" && e.status?.type === "waiting",
    ).length;

// A submit while a turn is in flight: the message shows at once, waiting.
export const sendWhileWorking = (
    conversationId: string,
    session: ChatSession,
    text: string,
    location: AuthorLocation | null,
): Result.Result<ChatDecision, ChatError> => {
    if (waitingCount(session.conversation) >= Core.Limits.WAITING_MESSAGES_MAX)
        return Result.err({ type: "waiting_full", conversationId });
    const entryId = turnLocalIds(conversationId, session).entryId;
    return Result.ok({
        events: [
            { type: "waiting_message_posted", conversationId, entryId, text },
        ],
        effects: [
            {
                type: "send_waiting_message",
                conversationId,
                entryId,
                text,
                ...(location === null ? {} : { location }),
            },
        ],
        signals: [],
    });
};

export const decideWaiting = (
    state: ChatState,
    command: WaitingCommand,
): ChatDecision => {
    const { conversationId } = command;
    switch (command.type) {
        case "waiting_message_accepted":
            return {
                events: [
                    {
                        type: "waiting_message_confirmed",
                        conversationId,
                        entryId: command.entryId,
                        messageId: command.messageId,
                    },
                ],
                effects: [],
                signals: [],
            };
        case "waiting_action_settled":
            return { events: [], effects: [], signals: [] };
        // Refused (the ceiling, or the work ended meanwhile): the text goes
        // back to the composer, nothing is lost.
        case "waiting_message_refused":
            return {
                events: [
                    {
                        type: "waiting_message_returned",
                        conversationId,
                        entryId: command.entryId,
                        text: command.text,
                    },
                ],
                effects: [],
                signals: [],
            };
        case "waiting_taken_back_to_edit":
            return {
                events: [
                    {
                        type: "waiting_texts_drafted",
                        conversationId,
                        texts: command.texts,
                    },
                ],
                effects: [],
                signals: [],
            };
        // The texts, in the order the messages wait.
        case "requested_waiting_edit":
            return {
                events: [],
                effects: [
                    {
                        type: "edit_waiting",
                        conversationId,
                        messageIds: command.messageIds,
                        texts: chatSessionFor(
                            state.sessions,
                            conversationId,
                        ).conversation.flatMap((e) =>
                            e.type === "author" &&
                            command.messageIds.includes(e.id)
                                ? [e.text]
                                : [],
                        ),
                    },
                ],
                signals: [],
            };
        case "requested_waiting_send_now":
            return action("send_waiting_now", command);
        case "requested_waiting_delete":
            return action("delete_waiting", command);
        case "requested_waiting_undo":
            return action("undo_waiting_delete", command);
        default:
            return exhaustSwitchCase(command);
    }
};

const action = (
    type: "send_waiting_now" | "delete_waiting" | "undo_waiting_delete",
    command: {
        readonly conversationId: string;
        readonly messageIds: readonly [string, ...string[]];
    },
): ChatDecision => ({
    events: [],
    effects: [
        {
            type,
            conversationId: command.conversationId,
            messageIds: command.messageIds,
        },
    ],
    signals: [],
});

// A turn-stream event about the waiting messages.
export const isWaitingEvent = (
    event: TurnStreamEvent,
): event is TurnWaitingEvent => event.type.startsWith("waiting_");

// The waiting messages are the queue, not the stopped turn's output: after a
// Stop they still change (they go out as the next turn), so they pass the
// late-event fence. Read while this page thinks the work is over (it was
// stopped, or failed): the messages went out as the next turn, so follow it
// (#100's follow path).
export const waitingProgress = (
    state: ChatState,
    command: {
        readonly conversationId: string;
        readonly event: TurnWaitingEvent;
        readonly ids: TurnStreamIds;
    },
): ChatDecision => {
    const { conversationId } = command;
    const folded: ChatEvent = {
        type: "turn_stream_folded",
        conversationId,
        event: command.event,
        ids: command.ids,
    };
    const session = chatSessionFor(state.sessions, conversationId);
    if (command.event.type !== "waiting_read" || session.drafting)
        return { events: [folded], effects: [], signals: [] };
    return {
        events: [folded, { type: "turn_followed", conversationId }],
        effects: [
            {
                type: "follow_turn",
                conversationId,
                ids: { activityId: resumeActivityId(conversationId, session) },
            },
        ],
        signals: [],
    };
};
